import { CustomLink, Footer } from '@teko/ecommerce';
import { Topbar, TopbarButton } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { LazyImage, styled } from '@teko/ui';
import { ForceLogin } from 'components/ForceLogin';
import { InlineBanner } from 'components/InlineBanner';
import { LazyComponent } from 'components/LazyComponent';
import { useTenantConfig } from 'hooks';
import { StringParam, useQuery } from 'hooks/useQuery';
import { t } from 'i18n';
import { ChatbotSidebarLayout } from 'modules/chatbot/components/ChatbotSidebarLayout';
import { ChatbotStoreSync } from 'modules/chatbot/components/ChatbotStoreSync';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect, useState } from 'react';
import { JSONObject } from 'types';
import { INLINE_BANNER_CLASSNAME } from '../../../../constants';
import { FloatingIconList } from './components/FloatingIconList';
import { NavigationBar } from './components/NavigationBar';

const PbLayout = dynamic(() =>
  import('modules/page-builder/layout').then((mod) => mod.PbLayout),
);

const TopBannerHeight = styled.div`
  height: 56px;
  @media print {
    display: none;
  }
`;

interface CustomLayoutProps extends LayoutProps {
  leftMenu?: Array<ReactNode>;
  rightMenu?: Array<ReactNode>;
  footer?: React.ReactNode;
  navigationBar?: React.ReactNode;
  forceLogin?: boolean;
}

// Adds the chatbot sidebar to the layout via the shared ChatbotSidebarLayout.
// When the chatbot is disabled (tenant has no chatbotConfig, or the user is not a teko
// account), ChatbotSidebarLayout renders children as-is — the original layout is preserved.
function ChatbotPageContent({
  children,
  footer,
  isChatbotPage,
}: {
  children: React.ReactNode;
  footer: React.ReactNode;
  isChatbotPage: boolean;
}) {
  return (
    <ChatbotSidebarLayout>
      <div>{children}</div>
      {!isChatbotPage && <LazyComponent>{footer}</LazyComponent>}
    </ChatbotSidebarLayout>
  );
}

const DefaultLayout: React.FC<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  forceLogin = false,
  layoutConfig,
  pageProps,
  inlineBanners,
}: CustomLayoutProps) => {
  const LoginWrapper = forceLogin ? ForceLogin : React.Fragment;
  const { partner, footerDataConfig, noOverride, epicPb1887 } =
    useTenantConfig();
  const { pathname } = useRouter();
  const isChatbotPage = pathname.endsWith('/chatbot');
  const topBanner = banners?.[0];

  if (epicPb1887) {
    return (
      <>
        <PbLayout config={layoutConfig || {}} pageProps={pageProps}>
          <LoginWrapper>
            {children}
            <FloatingIconList />
          </LoginWrapper>
        </PbLayout>
        {/* No-op when the tenant has no chatbotConfig */}
        <ChatbotStoreSync />
      </>
    );
  }

  if ((!footer && footerData) || noOverride) {
    const footerLayout = noOverride
      ? {
          ...footerData,
          supportAndPolicy: [
            ...(footerDataConfig?.['supportAndPolicy' as keyof JSONObject] ??
              []),
          ],
        }
      : footerData;
    footer = <Footer footer={footerLayout ?? {}} />;
  }
  const renderContent = () => {
    return (
      <>
        {!partner && (
          <>
            {topBanner && (
              <TopBannerHeight
                {...getTrackingData({
                  regionName: 'topBanner',
                  isTrackContent: true,
                })}
              >
                <CustomLink targetPath={topBanner.targetPath}>
                  <LazyImage
                    imageWidth={1920}
                    height={56}
                    cover
                    src={topBanner.image}
                  />
                </CustomLink>
              </TopBannerHeight>
            )}
            <InlineBanner
              inlineBanners={inlineBanners}
              inlineBannerClass={INLINE_BANNER_CLASSNAME.INLINE_HOME_TOP}
            />
            {(leftMenu || rightMenu) && (
              <Topbar leftMenu={leftMenu} rightMenu={rightMenu} />
            )}
          </>
        )}
        {navigationBar}
        <ChatbotPageContent isChatbotPage={isChatbotPage} footer={footer}>
          {children}
        </ChatbotPageContent>
      </>
    );
  };

  return (
    <>
      <LoginWrapper>{renderContent()}</LoginWrapper>
      {/* Không làm gì nếu tenant không có chatbotConfig */}
      <ChatbotStoreSync />
      <FloatingIconList />
    </>
  );
};
export const Layout: React.FunctionComponent<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  menuItems,
  liteLogo,
  forceLogin = false,
  layoutAreas,
  ...props
}) => {
  const { identityRequired, hasNews } = useTenantConfig();
  const [requireLogin, setRequireLogin] = useState(forceLogin);
  const finalRightActions: Array<ReactNode> = hasNews
    ? [
        <TopbarButton key={0} href="/tin-tuc">
          {t('Tin tức')}
        </TopbarButton>,
        ...(rightMenu ?? []),
      ]
    : rightMenu ?? [];
  const query = useQuery({ ref_code: StringParam });

  useEffect(() => {
    if (identityRequired && query?.ref_code) {
      sessionStorage.setItem('ref_code', query.ref_code.toString());
      setRequireLogin(true);
    } else if (!!sessionStorage.getItem('ref_code')) {
      // for case redirected back after login
      setRequireLogin(true);
    }
  }, []);

  if (!navigationBar) {
    navigationBar = (
      <NavigationBar
        menuItems={menuItems}
        showShippingFeeMiniCart={false}
        liteLogo={liteLogo}
      />
    );
  }
  return (
    <DefaultLayout
      leftMenu={leftMenu}
      rightMenu={finalRightActions}
      navigationBar={navigationBar}
      footer={footer}
      footerData={footerData}
      banners={banners}
      forceLogin={requireLogin}
      layoutAreas={layoutAreas}
      {...props}
    >
      {children}
    </DefaultLayout>
  );
};
